import { artBoardActions } from '@store/reducers/artboard'
import { thunkDragStop } from '@store/reducers/artboard/actionCreators'
import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { useArtboardMode, useDragToEdge, useScaleZoom } from '@/hooks'
import type { AppState } from '@/types'
import BlockOverlay from './components/BlockOverlay'
import NodeOverlay from './components/NodeOverlay'
import SymbolOverlay from './components/SymbolOverlay'
import css from './DragAndDropOverlay.module.scss'
import { type COORDINATES, DRAGGABLE_TYPE, type DROPZONE_TYPE } from './types'

export const DRAGGED_ATTRIBUTE = 'data-dragged-item'
export const DRAGGED_ATTRIBUTE_ID = 'data-dragged-id'
export const DRAGGED_ATTRIBUTE_TYPE = 'data-dragged-type'
export const DRAGGED_ATTRIBUTE_DATA = 'data-dragged-data'

export const DROPZONE_ATTRIBUTE = 'data-dropzone-item'
export const DROPZONE_ATTRIBUTE_ID = 'data-dropzone-id'
export const DROPZONE_ATTRIBUTE_TYPE = 'data-dropzone-type'
export const DROPZONE_ATTRIBUTE_DATA = 'data-dropzone-data'

const POSITION_DELAY = 4

const DragAndDropOverlay: React.FC = () => {
  const body = useRef(document.body)
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const { scale } = useScaleZoom()
  const { seoMode } = useArtboardMode()
  const suppressClick = useRef(false)

  const isStarted = useSelector<AppState, boolean>(
    ({ artboard }) => artboard.dragAndDrop.isStarted
  )

  const isActive = useSelector<AppState, boolean>(
    ({ artboard }) => artboard.dragAndDrop.isActive
  )

  const type = useSelector<AppState, DRAGGABLE_TYPE | null>(
    ({ artboard }) => artboard.dragAndDrop.dragged.type
  )

  const activeDropZoneId = useSelector<AppState, string | null>(
    ({ artboard }) => artboard.dragAndDrop.dropZone.id
  )

  const elementPosition = useSelector<AppState, COORDINATES>(
    ({ artboard }) => artboard.dragAndDrop.elementPosition
  )

  const startPosition = useSelector<AppState, COORDINATES>(
    ({ artboard }) => artboard.dragAndDrop.startPosition
  )

  const [isClearedSelections, setIsClearedSelections] = useState(false)
  const [hasClearedDropzone, setHasClearedDropzone] = useState(false)

  const [offset, setOffset] = useState<COORDINATES>({
    x: 0,
    y: 0,
  })

  const dragToEdge = useDragToEdge(isStarted)

  useEffect(() => {
    const classType = `--is-dragging-${type}`

    if (isActive && !body.current.classList.contains(classType)) {
      body.current.classList.add(classType)
    }

    return () => {
      body.current.classList.remove('--is-dragging-node')
      body.current.classList.remove('--is-dragging-block')
      body.current.classList.remove('--is-dragging-symbol')
    }
  }, [isActive, type])

  const onPointerMove = (e: PointerEvent) => {
    if (isStarted) {
      const x = e.clientX - startPosition.x
      const y = e.clientY - startPosition.y

      dragToEdge(e as any)

      if (
        !isActive &&
        (Math.abs(x) >= POSITION_DELAY || Math.abs(y) >= POSITION_DELAY)
      ) {
        dispatch(artBoardActions.dragSetActive())
        setIsClearedSelections(false)
      }

      if (isActive) {
        if (!isClearedSelections) {
          dispatch({
            type: CanvasActionTypes.CLEAR_ALL_SELECTION,
          })
          setIsClearedSelections(true)
        }

        setOffset({
          x,
          y,
        })

        const overElements = document.elementsFromPoint(e.clientX, e.clientY)

        const dropZoneElement = overElements.find(
          (item) => item.getAttribute(DROPZONE_ATTRIBUTE) === 'true'
        )

        if (dropZoneElement) {
          const dropZoneElementId = dropZoneElement.getAttribute(
            DROPZONE_ATTRIBUTE_ID
          )

          if (dropZoneElementId && activeDropZoneId !== dropZoneElementId) {
            const dropZoneType = dropZoneElement.getAttribute(
              DROPZONE_ATTRIBUTE_TYPE
            ) as DROPZONE_TYPE

            const dropZoneData =
              dropZoneElement.getAttribute(DROPZONE_ATTRIBUTE_DATA) || '{}'

            if (dropZoneType) {
              dispatch(
                artBoardActions.dragSetDropzone({
                  id: dropZoneElementId,
                  type: dropZoneType,
                  data: JSON.parse(dropZoneData),
                })
              )
            }

            setHasClearedDropzone(false)
          }
        } else if (!hasClearedDropzone) {
          dispatch(artBoardActions.dragResetDropzone())
          setHasClearedDropzone(true)
        }
      }
    }
  }

  const onPointerUp = () => {
    if (isStarted) {
      // pointerup resets DnD before the browser sends click, possibly to a
      // different card after reparenting. Keep that click out of the SEO panel.
      suppressClick.current = seoMode && isActive
      dispatch(thunkDragStop())
    }
  }

  const onPointerDown = () => {
    suppressClick.current = false
  }

  const onPointerCancel = () => {
    suppressClick.current = false
    if (isStarted) {
      dispatch(artBoardActions.dragStop())
    }
  }

  const onClick = (event: MouseEvent) => {
    if (suppressClick.current) {
      suppressClick.current = false
      event.preventDefault()
      event.stopPropagation()
    }
  }

  useEffect(() => {
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('pointermove', onPointerMove)
    document.addEventListener('pointerup', onPointerUp)
    document.addEventListener('pointercancel', onPointerCancel)
    document.addEventListener('click', onClick, true)

    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('pointermove', onPointerMove)
      document.removeEventListener('pointerup', onPointerUp)
      document.removeEventListener('pointercancel', onPointerCancel)
      document.removeEventListener('click', onClick, true)
    }
  })

  useEffect(() => {
    if (!isStarted) {
      setOffset({
        x: 0,
        y: 0,
      })
    }
  }, [isStarted])

  return (
    <div className={css.overlay}>
      {isActive && (
        <>
          <div
            className={css.overlayDraggedElement}
            style={{
              top: `${elementPosition.y}px`,
              left: `${elementPosition.x}px`,
              transform: `translateX(${offset.x}px) translateY(${offset.y}px)`,
            }}
          >
            <div
              className={css.overlayDraggedElementScale}
              style={{
                transform: `scale(${scale})`,
              }}
            >
              {type === DRAGGABLE_TYPE.PAGE && <NodeOverlay />}
              {type === DRAGGABLE_TYPE.BLOCK && <BlockOverlay />}
              {type === DRAGGABLE_TYPE.SYMBOL && <SymbolOverlay />}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default DragAndDropOverlay
